// src/components/exam-player/renderers/SelectSentenceRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function deriveInitialSelectedIds(initialAnswers) {
  if (!initialAnswers) return [];
  if (
    typeof initialAnswers === "string" ||
    typeof initialAnswers === "number"
  ) {
    return [String(initialAnswers)];
  }
  if (Array.isArray(initialAnswers)) {
    return initialAnswers.map((x) => String(x));
  }
  if (initialAnswers && typeof initialAnswers === "object") {
    if (Array.isArray(initialAnswers.selectedIds)) {
      return initialAnswers.selectedIds.map((x) => String(x));
    }
    if (Array.isArray(initialAnswers.selected)) {
      return initialAnswers.selected.map((x) => String(x));
    }
    if (initialAnswers.selectedId != null) {
      return [String(initialAnswers.selectedId)];
    }
  }
  return [];
}

export default function SelectSentenceRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  reviewMode,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const selectionMode = body.selectionMode || "single";

  const passageFa = body?.passage?.fa || "";
  const passageEn = body?.passage?.en || "";

  const sentences = useMemo(
    () => (Array.isArray(body.sentences) ? body.sentences : []),
    [body.sentences],
  );

  const [selectedIds, setSelectedIds] = useState(() =>
    deriveInitialSelectedIds(initialAnswers),
  );

  // فقط وقتی آیتم عوض می‌شود initialAnswers را دوباره اعمال کن
  useEffect(() => {
    setSelectedIds(deriveInitialSelectedIds(initialAnswers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id]);

  const toggleSentence = (id) => {
    if (reviewMode) return;

    setSelectedIds((prev) => {
      const sid = String(id);

      let next;
      if (selectionMode === "single") {
        if (prev.length === 1 && prev[0] === sid) {
          next = [];
        } else {
          next = [sid];
        }
      } else {
        if (prev.includes(sid)) {
          next = prev.filter((x) => x !== sid);
        } else {
          next = [...prev, sid];
        }
      }

      // همین‌جا به parent اطلاع بده (فقط وقتی user کلیک کرده)
      onAnswerChange?.({ selectedIds: next });
      return next;
    });
  };

  if (!sentences.length) {
    return (
      <div className="text-sm text-red-500">
        {t(
          "select_sentence.preview_no_sentences",
          "برای این سؤال هیچ جمله‌ای تعریف نشده است.",
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Passage */}
      <div className="prose prose-sm max-w-none mb-3">
        <div
          dangerouslySetInnerHTML={{
            __html: item?.stem?.fa || item?.stem?.en || t("preview.no_stem"),
          }}
        />
      </div>
      <div
        className="border rounded-lg p-3 bg-gray-50 text-sm leading-relaxed whitespace-pre-wrap"
        dir={isRtl ? "rtl" : "ltr"}
      >
        {passageFa || passageEn}
      </div>

      {/* Sentences list */}
      <div className="space-y-2">
        {sentences.map((s) => {
          const id = String(s.id);
          const label =
            s?.text?.fa ||
            s?.fa ||
            s?.text?.en ||
            s?.en ||
            t("select_sentence.untitled_sentence", "جمله بدون متن");

          const isSelected = selectedIds.includes(id);

          return (
            <button
              key={id}
              type="button"
              onClick={() => toggleSentence(id)}
              className={[
                "w-full text-left border rounded-lg px-3 py-2 text-sm transition-colors",
                isSelected
                  ? "bg-indigo-50 border-indigo-400"
                  : "bg-white hover:bg-gray-50 border-gray-200",
              ].join(" ")}
              dir={isRtl ? "rtl" : "ltr"}
            >
              <span className="font-semibold text-xs text-gray-500 mr-2">
                {id}
              </span>
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
